import React, { useState, useEffect } from 'react';
import { 
  DollarSign,
  Calendar,
  Download,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  CreditCard,
  TrendingUp,
  Filter,
  Search
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const TeacherSettlements = () => {
  const { currentUser, userData, userProfile } = useRealtime();
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingAmount: 0,
    paidAmount: 0,
    thisMonthEarnings: 0
  });

  useEffect(() => {
    if (currentUser?.uid) {
      loadSettlements();
    }
  }, [currentUser]);

  const loadSettlements = async () => {
    try {
      setLoading(true);
      
      // Load settlements from teacherSettlements collection
      const settlementsQuery = query(
        collection(db, 'teacherSettlements'),
        where('teacherId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(settlementsQuery);
      const settlementsList = [];
      
      snapshot.forEach((doc) => {
        settlementsList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setSettlements(settlementsList);
      calculateStats(settlementsList);
      
    } catch (error) {
      console.error('Error loading settlements:', error);
      
      // Fallback: Load from user profile
      if (userProfile?.teacherProfile?.settlements) {
        setSettlements(userProfile.teacherProfile.settlements);
        calculateStats(userProfile.teacherProfile.settlements);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (settlementsList) => {
    const totalEarnings = settlementsList.reduce((sum, settlement) => sum + (settlement.amount || 0), 0);
    const pendingAmount = settlementsList
      .filter(s => s.status === 'pending')
      .reduce((sum, settlement) => sum + (settlement.amount || 0), 0);
    const paidAmount = settlementsList
      .filter(s => s.status === 'paid')
      .reduce((sum, settlement) => sum + (settlement.amount || 0), 0);
    
    // Calculate this month's earnings
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonthEarnings = settlementsList
      .filter(s => {
        const settlementDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
        return settlementDate.getMonth() === currentMonth && settlementDate.getFullYear() === currentYear;
      })
      .reduce((sum, settlement) => sum + (settlement.amount || 0), 0);

    setStats({
      totalEarnings,
      pendingAmount,
      paidAmount,
      thisMonthEarnings
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
        return <CreditCard className="w-4 h-4" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const filteredSettlements = settlements.filter(settlement => {
    const matchesSearch = settlement.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         settlement.period?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || settlement.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const settlementDate = date.toDate ? date.toDate() : new Date(date);
    return settlementDate.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <DollarSign className="w-6 h-6 mr-2 text-green-600" />
          Payment Settlements
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Track your earnings, payments, and settlement history
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(stats.totalEarnings)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pending Amount</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {formatCurrency(stats.pendingAmount)}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Paid Amount</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(stats.paidAmount)}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">This Month</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatCurrency(stats.thisMonthEarnings)}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search settlements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Settlements List */}
        {filteredSettlements.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Settlements Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {settlements.length === 0 
                ? "You don't have any settlements yet"
                : "No settlements match your search criteria"
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSettlements.map((settlement) => (
              <div key={settlement.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {settlement.description || `Settlement for ${settlement.period}`}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Period: {settlement.period} • Created: {formatDate(settlement.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(settlement.amount)}
                    </p>
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(settlement.status)}`}>
                      {getStatusIcon(settlement.status)}
                      <span className="capitalize">{settlement.status}</span>
                    </div>
                  </div>
                </div>

                {settlement.details && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Classes Taken</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {settlement.details.classesTaken || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Rate per Class</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {settlement.details.ratePerClass ? formatCurrency(settlement.details.ratePerClass) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Bonus/Deductions</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {settlement.details.bonus ? formatCurrency(settlement.details.bonus) : '₹0'}
                      </p>
                    </div>
                  </div>
                )}

                {settlement.paymentDetails && settlement.status === 'paid' && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Payment Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-green-700 dark:text-green-300">Payment Method</p>
                        <p className="font-medium text-green-900 dark:text-green-100">
                          {settlement.paymentDetails.method || 'Bank Transfer'}
                        </p>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-300">Transaction ID</p>
                        <p className="font-medium text-green-900 dark:text-green-100">
                          {settlement.paymentDetails.transactionId || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-300">Paid On</p>
                        <p className="font-medium text-green-900 dark:text-green-100">
                          {formatDate(settlement.paymentDetails.paidAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors">
                    <Eye className="w-4 h-4 inline mr-1" />
                    View Details
                  </button>
                  {settlement.status === 'paid' && (
                    <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm transition-colors">
                      <Download className="w-4 h-4 inline mr-1" />
                      Download Receipt
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherSettlements;